import { useQuery } from '@apollo/client';
import { Flags } from '@constants/flags';
import { Frequency } from '@generated/graphql';
import { useFlag } from '@onevesthq/feature-flags/web';
import { ISO_DATE_FORMAT } from '@ovComponents/4-module/workflowCompletion/constants/dateFormats';
import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { UserContext } from '@providers/userContextProvider';
import dayjs, { type ConfigType } from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { FETCH_USER_LOCALIZATION } from '../queries';
import {
  type DepositScheduleOptions,
  DepositStarting,
  isScheduledDateEditable,
  resolveDefaultScheduleOption,
  shouldDefaultToFutureDate,
  TRANSFER_TYPES_WITHOUT_SCHEDULE,
} from './depositSchedule';
import { useDepositScheduledDateChecks } from './useDepositScheduledDateChecks';

type UseDepositScheduleProp = {
  options?: DepositScheduleOptions | null;
  deposit: TransferContextData;
  updateContextWithFields: (
    fields: Partial<TransferContextData>,
  ) => Promise<void>;
  userId?: string;
};

type TransferCountrySources = {
  bankAccount?: TransferContextData['bankAccount'];
  user?: {
    physicalAddress?: { country?: string | null } | null;
    countryOfTaxResidence?: string | null;
  } | null;
  organizationCountries: string[];
};

const resolveTransferCountry = ({
  bankAccount,
  user,
  organizationCountries,
}: TransferCountrySources) =>
  bankAccount?.bankAccountTransitPhysicalAddress?.country ||
  user?.physicalAddress?.country ||
  user?.countryOfTaxResidence ||
  organizationCountries[0];

export const useDepositSchedule = ({
  options,
  deposit,
  updateContextWithFields,
  userId,
}: UseDepositScheduleProp) => {
  const { activeOrganization } = useContext(UserContext);
  const { data: localizationData } = useQuery(FETCH_USER_LOCALIZATION, {
    variables: { userId },
    skip: !userId,
  });

  const defaultToFutureDate = shouldDefaultToFutureDate({
    deposit,
    options,
  });

  const [starting, setStarting] = useState<DepositStarting>(
    deposit.schedule === Frequency.OneTime && defaultToFutureDate
      ? DepositStarting.FutureDate
      : DepositStarting.Now,
  );

  useEffect(() => {
    if (defaultToFutureDate) {
      setStarting(DepositStarting.FutureDate);
    } else {
      setStarting(DepositStarting.Now);
    }
  }, [defaultToFutureDate]);

  const defaultScheduleOption = resolveDefaultScheduleOption(options);

  const transferCountry = resolveTransferCountry({
    bankAccount: deposit.bankAccount,
    user: localizationData?.fetchUser?.user,
    organizationCountries: activeOrganization.applicableLocalization.countries,
  });

  const { enabled: weekdaysOnlyCalendar } = useFlag(
    Flags.weekdays_only_calendar,
    {
      default: false,
    },
  );

  const isScheduledDateDisabled = (date: ConfigType) =>
    !isScheduledDateEditable({
      date,
      schedule: deposit.schedule,
      weekdaysOnlyCalendar,
      transferCountry,
    });

  const { shouldClearNewDate, ...scheduledDateChecks } =
    useDepositScheduledDateChecks({
      scheduledDate: deposit.scheduledDate,
      country: transferCountry,
      options,
      isEft: deposit.type === DEPOSIT_TRANSFER_TYPE.EFT,
      isScheduled:
        deposit.schedule !== Frequency.OneTime ||
        starting !== DepositStarting.Now,
    });

  useEffect(() => {
    if (deposit.schedule !== Frequency.OneTime) {
      updateContextWithFields({
        schedule: deposit.schedule ?? defaultScheduleOption,
      });
    }
    if (
      deposit.schedule === Frequency.OneTime &&
      starting === DepositStarting.Now
    ) {
      updateContextWithFields({ scheduledDate: undefined });
    }
    const type = deposit.type;
    if (type && TRANSFER_TYPES_WITHOUT_SCHEDULE.includes(type)) {
      updateContextWithFields({
        scheduledDate: undefined,
        schedule: Frequency.OneTime,
      });
    }
  }, [deposit.schedule, starting, options]);

  const changeScheduledDate = async (date: ConfigType) => {
    const formattedDate = dayjs(date?.toString()).format(ISO_DATE_FORMAT);
    const scheduledDate = (await shouldClearNewDate(date))
      ? undefined
      : formattedDate;
    await updateContextWithFields({ scheduledDate });
  };

  return {
    starting,
    setStarting,
    defaultScheduleOption,
    isScheduledDateDisabled,
    changeScheduledDate,
    ...scheduledDateChecks,
  };
};
